<template>
    <div class="close-header-title no-con">
        <section class="privacy-section">
            <div class="container">
                <div class="main-heading">
                    <h1>STANDARD CONTRACTUAL CLAUSES (SCCs) - CROSS-BORDER TRANSFERS</h1>
                    <p>Last updated: 7 September 2025</p>
                    <p>These <a href="/standard-contractual-clauses" target="_blank">Standard Contractual Clauses</a>
                        (“SCC Terms” or “Clauses”) are adopted by Vanovy
                        (“Vanovy”, the “Data Exporter”, or the “Exporter”) for use with its third-party vendors
                        and service providers (each, a “Data Importer”, “Importer”, “Processor”, or, where applicable,
                        “Independent Controller”) to lawfully effect international transfers of personal data from the
                        European Economic Area (EEA), Switzerland, and the United Kingdom in accordance with applicable
                        data protection laws.</p>
                    <p>These <a href="/standard-contractual-clauses" target="_blank">SCC Terms</a> are incorporated by
                        reference into Vanovy’ agreements with vendors
                        (including master services agreements, order forms, statements of work, online signups, and data
                        processing agreements). Where these <a href="/standard-contractual-clauses" target="_blank">SCC
                            Terms</a> conflict with any other provision of those
                        agreements on data transfer matters, these <a href="/standard-contractual-clauses"
                            target="_blank">SCC Terms</a> prevail. Capitalized terms not defined here
                        have the meanings given in Vanovy’ <a href="/data-processing-agreement"
                            target="_blank">Data Processing Agreement</a> (DPA) or <a href="/privacy-policy"
                            target="_blank">Privacy Policy</a>.
                    </p>
                </div>
                <div class="content-section center">
                    <h2>ARTICLE I: PURPOSE, INCORPORATION, AND SCOPE</h2>
                    <h4>1.1 Purpose.</h4>
                    <p>The purpose of these <a href="/standard-contractual-clauses" target="_blank">SCC Terms</a> is to
                        operationalize the EU Commission Decision 2021/914 Standard
                        Contractual Clauses for transferring personal data to third countries and to layer in the UK
                        IDTA/UK Addendum and the Swiss FDPIC adaptations for the UK and Switzerland, respectively. These
                        <a href="/standard-contractual-clauses" target="_blank">SCC Terms</a> also set Vanovy’
                        expectations regarding Transfer Impact Assessments and
                        supplementary measures.
                    </p>
                    <h4>1.2 Scope.</h4>
                    <p>These <a href="/standard-contractual-clauses" target="_blank">SCC Terms</a> apply to all
                        transfers of personal data from the EEA, UK, and Switzerland by
                        Vanovy to the Importer where there is no other valid transfer tool that singularly
                        covers the flow (e.g., an adequacy decision). If an Importer maintains EU–US Data Privacy
                        Framework (DPF) certification (for applicable data), Vanovy may rely on DPF as a
                        separate transfer mechanism for eligible flows, while retaining SCCs to cover non-DPF data or
                        circumstances.</p>

                    <h2>ARTICLE II: DEFINITIONS AND CONSTRUCTION</h2>
                    <h4>2.1 Definitions.</h4>
                    <p>Terms such as controller, processor, processing, personal data, special categories of data, data
                        subject, personal data breach, and supervisory authority have the meanings given by the GDPR
                        (and analogously under UK GDPR and Swiss law). “EU SCCs” refers to the 2021/914 Clauses (Modules
                        1–4). “UK Addendum/IDTA” refers to the UK’s International Data Transfer Addendum or the
                        International Data Transfer Agreement. “Swiss Addendum” means the FDPIC-aligned adaptation for
                        Swiss personal data. “TIA” means transfer impact assessment.</p>
                    <h4>2.2 Construction.</h4>
                    <p>To the extent of ambiguity, these <a href="/standard-contractual-clauses" target="_blank">SCC
                            Terms</a> shall be interpreted to maximize the legality of
                        transfers and protect the Exporter’s compliance posture, without expanding the Importer’s rights
                        beyond what the Clauses expressly provide.</p>

                    <h2>ARTICLE III: MODULE SELECTION AND APPLICABILITY</h2>
                    <h4>3.1 Default Module Use.</h4>
                    <p>Vanovy will select the correct SCC Module per relationship and processing role:</p>
                    <p>- Module 2 (Controller → Processor): the default for most vendors handling personal data on
                        behalf of Vanovy (e.g., Hostinger hosting, Stripe payment processing, Google Analytics
                        where configured as a processor).</p>
                    <p>- Module 1 (Controller → Controller): used where the vendor acts as an independent controller for
                        specific, limited purposes consistent with Vanovy’ instructions (e.g., where an
                        analytics or anti-fraud provider operates as an independent controller for security/fraud
                        signals).</p>
                    <h4>3.2 Mixed Roles.</h4>
                    <p>Where a vendor performs both controller and processor functions under different service features,
                        Vanovy may execute both Modules (1 and 2) and map each data flow in ARTICLE XXIII(B)
                        with precision.</p>
                    <h4>3.3 Module Non-Applicability.</h4>
                    <p>Module 3 (Processor→Processor) or Module 4 (Processor→Controller) may be used where Vanovy’
                        contracted processor (acting on our behalf) exports data to its own sub-processor or
                        returns data to a controller. If invoked, those modules will be documented in the relevant
                        vendor chain and flowed down contractually.</p>

                    <h2>ARTICLE IV: RELATIONSHIP WITH THE DPA AND OTHER TERMS</h2>
                    <h4>4.1 Hierarchy.</h4>
                    <p>For data transfer matters, these <a href="/standard-contractual-clauses" target="_blank">SCC
                            Terms</a> supersede conflicting provisions in the Main Agreement
                        or DPA. For processing and security obligations not specifically dictated by the SCCs, the DPA
                        governs. Where applicable, the UK Addendum/IDTA and Swiss Addendum take precedence as required
                        by their own terms.</p>
                    <h4>4.2 No Dilution.</h4>
                    <p>The Importer may not rely on any limitation, disclaimer, or cap in the Main Agreement to avoid or
                        reduce obligations arising from the Clauses to the extent such reliance would render the Clauses
                        ineffective under EU/UK/Swiss law.</p>

                    <h2>ARTICLE V: GENERAL OBLIGATIONS OF THE PARTIES</h2>
                    <h4>5.1 Exporter (Vanovy).</h4>
                    <p>Vanovy determines the purposes and means of processing, supplies accurate ARTICLE XXIII
                        details, and issues lawful instructions. Vanovy shall inform the Importer if it becomes
                        aware of inaccuracies or restrictions affecting the data transfer.</p>
                    <h4>5.2 Importer (Vendor).</h4>
                    <p>The Importer shall process personal data only on the Exporter’s documented instructions (Module
                        2), or within the compatible purposes set out in ARTICLE XXIII (Module 1). The Importer shall
                        promptly notify the Exporter where an instruction appears to conflict with applicable law or the
                        Clauses.</p>
                    <h4>5.3 Transparency.</h4>
                    <p>The Importer shall make information necessary to demonstrate compliance with the Clauses
                        available to the Exporter upon request, including relevant third-party assurance reports (e.g.,
                        ISO 27001, SOC 2), security whitepapers, and sub-processor listings.</p>

                    <h2>ARTICLE VI: SECURITY OF PROCESSING</h2>
                    <h4>6.1 Technical and Organizational Measures.</h4>
                    <p>The Importer must implement appropriate measures considering the state of the art, implementation
                        costs, and risk to the rights and freedoms of natural persons. ARTICLE XXIV enumerates baseline
                        and service-specific controls Vanovy expects, including encryption in transit and at
                        rest, key segregation, least-privilege access, MFA, network segmentation, logging/SIEM,
                        vulnerability remediation, BCP/DR, and sub-processor oversight.</p>
                    <h4>6.2 No Degradation.</h4>
                    <p>The Importer shall not materially reduce security during the term and will notify Vanovy
                        of any significant changes that could adversely affect confidentiality, integrity, or
                        availability.</p>
                    <h4>6.3 Breach Notification.</h4>
                    <p>The Importer shall notify Vanovy without undue delay and, where feasible, within 24 hours
                        of discovering a personal data breach, with the content elements set out in Article VII of the
                        DPA.</p>

                    <h2>ARTICLE VII: DATA SUBJECT RIGHTS</h2>
                    <h4>7.1 Assistance.</h4>
                    <p>The Importer shall assist Vanovy in fulfilling data subject requests (e.g., access,
                        rectification, erasure, restriction, portability, objection) by providing relevant data or
                        implementing technical measures, within legal timelines, at Vanovy’ reasonable expense
                        unless due to Importer’s non-compliance.</p>
                    <h4>7.2 Direct Requests.</h4>
                    <p>The Importer shall not respond to data subject requests unless instructed by Vanovy or
                        required by law, notifying Vanovy in advance where permitted.</p>

                    <h2>ARTICLE VIII: SUB-PROCESSING</h2>
                    <h4>8.1 Authorization.</h4>
                    <p>Vanovy grants general authorization for sub-processors necessary for the services,
                        provided the Importer complies with ARTICLE XXV (sub-processor register, notice, objection,
                        flow-down).</p>
                    <h4>8.2 Flow-Down.</h4>
                    <p>The Importer shall ensure sub-processors are bound by terms no less protective than these <a
                            href="/standard-contractual-clauses" target="_blank">SCC Terms</a>,
                        including SCCs or equivalent for restricted transfers.</p>

                    <h2>ARTICLE IX: AUDITS AND INSPECTIONS</h2>
                    <h4>9.1 Audit Rights.</h4>
                    <p>Vanovy (or its designated auditor, bound by confidentiality) may audit the Importer’s
                        compliance with these <a href="/standard-contractual-clauses" target="_blank">SCC Terms</a> up
                        to once annually, or more if required by a supervisory
                        authority or following a breach. Audits shall be at Vanovy’ expense, conducted with
                        reasonable notice (at least 30 days unless urgent), and minimize disruption.</p>
                    <h4>9.2 Documentation.</h4>
                    <p>The Importer shall provide certifications, attestations, and TIA documentation upon request to
                        demonstrate compliance.</p>

                    <h2>ARTICLE X: TRANSFER IMPACT ASSESSMENTS (TIAs)</h2>
                    <h4>10.1 Obligation.</h4>
                    <p>The Importer shall conduct a TIA for each restricted transfer, assessing the third country’s
                        legal framework, government access risks, and safeguards, and provide the TIA to Vanovy
                        upon request.</p>
                    <h4>10.2 Supplementary Measures.</h4>
                    <p>Where a TIA indicates risks, the Importer shall implement supplementary measures (e.g., E2E
                        encryption, pseudonymization, EU-held keys) to ensure an essentially equivalent level of
                        protection.</p>

                    <h2>ARTICLE XI: DATA RETURN AND DELETION</h2>
                    <h4>11.1 Obligation.</h4>
                    <p>Upon termination or Vanovy’ request, the Importer shall return or delete all transferred
                        personal data within 30 days (or shorter if required by law), unless retention is legally
                        mandated, in which case the Importer shall notify Vanovy of the basis and period.</p>
                    <h4>11.2 Certification.</h4>
                    <p>The Importer shall certify completion of return/deletion in writing, unless retention is
                        required.</p>

                    <h2>ARTICLE XII: GOVERNMENT ACCESS REQUESTS</h2>
                    <h4>12.1 Notification and Challenge.</h4>
                    <p>The Importer shall notify Vanovy of any government access request for transferred data
                        unless prohibited by law, and challenge requests that appear unlawful or disproportionate,
                        exhausting available remedies.</p>
                    <h4>12.2 Transparency.</h4>
                    <p>The Importer shall provide annual transparency reports (if legally permitted) on the frequency
                        and nature of government access requests.</p>

                    <h2>ARTICLE XIII: LIABILITY AND INDEMNITY</h2>
                    <h4>13.1 Importer Liability.</h4>
                    <p>The Importer shall indemnify Vanovy for losses, fines, or damages arising from the
                        Importer’s breach of these <a href="/standard-contractual-clauses" target="_blank">SCC
                            Terms</a>, including failure to implement required safeguards or
                        respond to data subject requests.</p>
                    <h4>13.2 No Caps.</h4>
                    <p>Liability caps in the Main Agreement do not apply to breaches of these <a
                            href="/standard-contractual-clauses" target="_blank">SCC Terms</a> where such caps
                        would undermine the Clauses’ effectiveness under EU/UK/Swiss law.</p>

                    <h2>ARTICLE XIV: TERMINATION</h2>
                    <h4>14.1 Termination for Breach.</h4>
                    <p>Vanovy may suspend or terminate data transfers if the Importer materially breaches these
                        <a href="/standard-contractual-clauses" target="_blank">SCC Terms</a> and fails to cure within
                        10 days of notice, or if a supervisory authority prohibits
                        the transfer.
                    </p>
                    <h4>14.2 Effect.</h4>
                    <p>Upon termination, the Importer shall comply with ARTICLE XI (return/deletion).</p>

                    <h2>ARTICLE XV: GOVERNING LAW AND JURISDICTION</h2>
                    <h4>15.1 EU SCCs.</h4>
                    <p>Governing law: Spain. Forum: Courts of Madrid, Spain, subject to data subjects’ rights under the
                        SCCs.</p>
                    <h4>15.2 UK Addendum/IDTA.</h4>
                    <p>Governing law: England & Wales, unless otherwise agreed.</p>
                    <h4>15.3 Swiss Addendum.</h4>
                    <p>Governed by Swiss law and forum per FDPIC alignment.</p>

                    <h2>ARTICLE XVI: ANNEXES AND INCORPORATION</h2>
                    <h4>16.1 EU SCC Annexes.</h4>
                    <p>The Annexes required by the EU SCCs (e.g., processing description, TOMs, sub-processors) are
                        incorporated via ARTICLES XXIII–XXV below.</p>
                    <h4>16.2 UK/Swiss Adaptations.</h4>
                    <p>The UK Addendum/IDTA and Swiss Addendum are incorporated as applicable, with details in ARTICLE
                        XXIII(C).</p>

                    <h2>ARTICLE XVII: SPECIAL CATEGORIES OF DATA</h2>
                    <h4>17.1 Restrictions.</h4>
                    <p>Vanovy does not intentionally transfer special categories of data (e.g., health,
                        religion, political opinions) unless explicitly agreed. If transferred, the Importer shall apply
                        heightened safeguards and process only per written instructions.</p>

                    <h2>ARTICLE XVIII: DATA PROTECTION IMPACT ASSESSMENTS (DPIAs)</h2>
                    <h4>18.1 Assistance.</h4>
                    <p>The Importer shall assist Vanovy with DPIAs by providing relevant information about its
                        processing and safeguards, at Vanovy’ reasonable expense unless due to Importer’s
                        non-compliance.</p>

                    <h2>ARTICLE XIX: CHANGES IN LAW</h2>
                    <h4>19.1 Updates.</h4>
                    <p>If changes in EU/UK/Swiss law require updates to these <a href="/standard-contractual-clauses"
                            target="_blank">SCC Terms</a>, the Importer shall promptly
                        cooperate to execute or acknowledge revised terms.</p>

                    <h2>ARTICLE XX: CONFIDENTIALITY</h2>
                    <h4>20.1 Obligations.</h4>
                    <p>The Importer shall ensure personnel processing transferred data are bound by confidentiality
                        obligations at least as protective as those in the DPA.</p>

                    <h2>ARTICLE XXI: NOTIFICATIONS</h2>
                    <h4>21.1 Communication.</h4>
                    <p>Notices under these <a href="/standard-contractual-clauses" target="_blank">SCC Terms</a> shall
                        be sent to the contacts in ARTICLE XXIII(D), with urgent
                        notices (e.g., breaches) requiring 24×7 monitoring.</p>

                    <h2>ARTICLE XXII: SEVERABILITY</h2>
                    <h4>22.1 Severability.</h4>
                    <p>If any provision is invalid, it shall be modified to the minimum extent necessary to make it
                        valid while preserving intent; the remainder remains in force.</p>

                    <h2>ARTICLE XXIII: SCC ANNEX I (DETAILS OF PROCESSING)</h2>
                    <h4>A. Parties and Roles</h4>
                    <p>Data Exporter: Vanovy (controller, operator of www.vanovy.com). Contact: <a
                            href="mailto:support@vanovy.com">support@vanovy.com</a>.</p>
                    <p>Data Importer: Vendor as identified in the Main Agreement/DPA (controller or processor per Module
                        selection).</p>
                    <h4>B. Description of Transfer</h4>
                    <p>Categories of data subjects: Guests, Hosts, platform users, vendor personnel interacting with
                        services.</p>
                    <p>Categories of personal data: Identifiers (name, email, IP address), contact details,
                        transactional metadata (booking/payment data), usage data (navigation, session logs), support
                        artifacts (ticket content).</p>
                    <p>Sensitive data: None intentionally transferred unless specified.</p>
                    <p>Frequency: Continuous for duration of services.</p>
                    <p>Nature/purpose: Processing for platform services (hosting, payments, analytics, support), fraud
                        prevention, security.</p>
                    <p>Duration: Term of Main Agreement plus retention/deletion period per DPA.</p>
                    <p>Module 1 purposes (if applicable): Limited to compatible purposes (e.g., security, fraud
                        detection) per Vanovy’ instructions.</p>
                    <h4>C. Competent Authority & Jurisdictional Selections</h4>
                    <p>Clause 17 governing law: Spain</p>
                    <p>Clause 18 forum: Courts of Madrid, Spain (subject to data subjects’ rights under the SCCs)</p>
                    <p>UK Addendum/IDTA: England & Wales (default unless otherwise agreed)</p>
                    <p>Swiss Addendum: Swiss law and forum per FDPIC alignment</p>
                    <h4>D. Contact Points For Data Breach And Privacy</h4>
                    <p>Exporter incident contact: <a
                            href="mailto:support@vanovy.com">support@vanovy.com</a></p>
                    <p>Importer incident contact: As specified in the Main Agreement/DPA (must be monitored 24×7 for
                        urgent notifications)</p>

                    <h2>ARTICLE XXIV: TECHNICAL & ORGANIZATIONAL MEASURES</h2>
                    <h4>A. Information Security Governance</h4>
                    <p>Written security program approved by leadership; documented risk assessments; designated security
                        and privacy officers; annual review of control effectiveness.</p>
                    <h4>B. Asset and Data Management</h4>
                    <p>Data classification and handling standards; minimization; restricted use of production data in
                        non-production environments; encryption in transit (TLS 1.2+) and at rest (AES-class); key
                        management with separation of duties and periodic rotation.</p>
                    <h4>C. Access Control and Authentication</h4>
                    <p>Centralized IAM; least-privilege and role-based access; multi-factor authentication for
                        privileged access; quarterly access reviews; immediate de-provisioning on role change/exit;
                        password policy consistent with NIST recommendations.</p>
                    <h4>D. Network and Platform Security</h4>
                    <p>Segmented networks; firewalls and security groups; WAF; DDoS protections; hardened OS and
                        container baselines; CIS benchmarks where applicable; secrets stored in vaults with rotation.
                    </p>
                    <h4>E. Secure Development and Change Management</h4>
                    <p>Secure SDLC; code reviews; SAST/DAST; dependency and container scanning; change approval
                        workflows; production changes logged and auditable; segregation of duties.</p>
                    <h4>F. Vulnerability and Patch Management</h4>
                    <p>Routine vulnerability scans; independent penetration tests at least annually; remediation SLAs
                        (e.g., critical &lt;7 days, high &lt;30 days); verified closure.</p>
                    <h4>G. Logging, Monitoring, and Incident Response</h4>
                    <p>Centralized logging (immutable where feasible); SIEM with alerting; time-sync across systems;
                        incident response plan with roles, runbooks, and periodic exercises; breach notification within
                        24 hours of discovery to Exporter.</p>
                    <h4>H. Business Continuity and Disaster Recovery</h4>
                    <p>Documented BCP/DR; geographically separate backups; encrypted backups; periodic restore tests;
                        defined RPO/RPO; capacity planning and failover testing for critical services.</p>
                    <h4>I. Physical and Environmental Security</h4>
                    <p>Certified data centers (e.g., ISO 27001/SOC 2); access badges with logging; CCTV; visitor
                        controls; hardware disposal per NIST SP 800-88 or equivalent.</p>
                    <h4>J. Supplier and Sub-Processor Management</h4>
                    <p>Due diligence on sub-processors (security, privacy, financial stability); contractual data
                        protection flow-down; ongoing monitoring; breach back-to-back obligations.</p>
                    <h4>K. Privacy by Design and Consent/Opt-Out Signaling</h4>
                    <p>Respect for Exporter’s consent/opt-out signals (including Global Privacy Control where relevant
                        to tools); suppression of optional analytics/ads features until Exporter indicates lawful basis;
                        configurable retention.</p>
                    <h4>L. Data Subject Request Support</h4>
                    <p>Capabilities to locate, export (machine-readable), rectify, and erase records associated with
                        data subjects; APIs or admin tools to support requests; audit trail of actions taken.</p>
                    <h4>M. International Transfers Safeguards</h4>
                    <p>TIA documented and reviewed; supplementary measures implemented (E2E encryption,
                        pseudonymization/tokenization with EU-held keys, split-processing, residency controls);
                        challenge of government demands.</p>
                    <h4>N. Training and Awareness</h4>
                    <p>Role-based security and privacy training at onboarding and annually; targeted phishing and
                        incident drills.</p>

                    <h2>ARTICLE XXV: LIST OF SUB-PROCESSORS (REFERENCE)</h2>
                    <h4>A. Sub-Processor Register.</h4>
                    <p>The Importer shall maintain a current register of sub-processors used for Vanovy’
                        personal data, identifying name, purpose, location, and contact, and provide 15 days’ prior
                        notice (email acceptable) before onboarding any new sub-processor.</p>
                    <h4>B. Objection Workflow.</h4>
                    <p>If Vanovy objects, the Importer will propose alternatives or allow suspension/termination
                        of affected processing with pro-rata fee adjustments.</p>
                    <h4>C. Flow-Down.</h4>
                    <p>The Importer shall impose SCCs and equivalent protections (including UK/Swiss adaptations) where
                        sub-processing entails further restricted transfers.</p>

                    <h2>ARTICLE XXVI: SPECIAL TERMS FOR SPECIFIC SERVICE PROFILES</h2>
                    <h4>26.1 Hosting/Infrastructure.</h4>
                    <p>Residency options should prefer EEA data centers for EEA data; logs minimized; support access JIT
                        gated; encryption at rest/in transit mandatory; DDoS/WAF active; formal penetration testing
                        annually.</p>
                    <h4>26.2 Payments.</h4>
                    <p>PAN/token handling per PCI DSS; 3-D Secure/SCA flows supported; fraud detection signals treated
                        as necessary processing; government access demands challenged; transfer tool stacking (DPF+SCCs)
                        permitted.</p>
                    <h4>26.3 Analytics/Measurement.</h4>
                    <p>IP anonymization/collection controls enabled; advertising features off unless the Exporter has a
                        lawful basis; event retention minimized; consent signals respected; Module 1 used if the
                        provider acts as independent controller for narrow security/anti-fraud purposes.</p>

                    <h2>ARTICLE XXVII: GENERAL</h2>
                    <h4>27.1 No Third-Party Publication.</h4>
                    <p>Neither Party will publish the signed Clauses; doing so is unnecessary and may create security
                        risk. Data subjects receive notice via <a href="/privacy-policy" target="_blank">Privacy
                            Policy</a>.</p>
                    <h4>27.2 Language and Counterparts.</h4>
                    <p>These <a href="/standard-contractual-clauses" target="_blank">SCC Terms</a> are in English.
                        Execution may be by electronic acceptance or counterparts. Copies
                        may be provided to regulators upon request.</p>
                    <h4>27.3 Changes in Law.</h4>
                    <p>If legal changes require updates to these Clauses, Vanovy may issue updated terms. The
                        Importer shall promptly execute or acknowledge updated forms to avoid transfer disruption.</p>

                    <h2>ARTICLE XXVIII: ACCEPTANCE AND EFFECT</h2>
                    <h4>28.1 Acceptance.</h4>
                    <p>By processing Vanovy personal data in a restricted transfer context, the Importer
                        accepts these <a href="/standard-contractual-clauses" target="_blank">SCC Terms</a>.</p>
                    <h4>28.2 Effect.</h4>
                    <p>These <a href="/standard-contractual-clauses" target="_blank">SCC Terms</a> take effect on the
                        later of (a) the effective date of the underlying agreement or
                        (b) the date the Importer first processes exported personal data subject to restricted transfer
                        rules.</p>

                    <h2>ACKNOWLEDGMENT</h2>
                    <p>By agreeing to these <a href="/standard-contractual-clauses" target="_blank">SCC Terms</a>, the
                        Importer confirms it has read, understood, and will comply
                        with the EU 2021/914 Clauses (Modules selected as specified), the UK IDTA/Addendum, and the
                        Swiss FDPIC adaptation, will maintain documented TIAs, and will implement supplementary measures
                        necessary to ensure an essentially equivalent level of protection for data subjects.</p>
                </div>
            </div>
        </section>
    </div>
</template>
<script>
export default {
    name: 'standardContractualClauses',
    data() {
        return {};
    },
    mounted() { },
    methods: {},
    computed: {},
    created() { }
}
</script>